"use client";
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

// Hook for scroll animations
export const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return { ref, isVisible };
};

// Base animated component
interface ScrollAnimatedProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  variant?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'slideRight' | 'scale' | 'stagger';
  staggerDelay?: number;
  index?: number;
}

const ScrollAnimated: React.FC<ScrollAnimatedProps> = ({
  children,
  delay = 0,
  className,
  variant = 'fadeIn',
  staggerDelay = 0,
  index = 0,
}) => {
  const { ref, isVisible } = useScrollAnimation();

  const getAnimationClasses = () => {
    const baseClasses = 'transition-all duration-700 ease-out';
    const delayClass = `delay-[${delay}ms]`;
    
    if (!isVisible) {
      switch (variant) {
        case 'fadeIn':
          return `${baseClasses} ${delayClass} opacity-0`;
        case 'slideUp':
          return `${baseClasses} ${delayClass} opacity-0 translate-y-8`;
        case 'slideLeft':
          return `${baseClasses} ${delayClass} opacity-0 translate-x-8`;
        case 'slideRight':
          return `${baseClasses} ${delayClass} opacity-0 -translate-x-8`;
        case 'scale':
          return `${baseClasses} ${delayClass} opacity-0 scale-95`;
        case 'stagger':
          const staggerDelayValue = delay + (index * staggerDelay);
          return `${baseClasses} delay-[${staggerDelayValue}ms] opacity-0 translate-y-4`;
        default:
          return `${baseClasses} ${delayClass} opacity-0`;
      }
    } else {
      return `${baseClasses} ${delayClass} opacity-100 translate-y-0 translate-x-0 scale-100`;
    }
  };

  return (
    <div
      ref={ref}
      className={cn(getAnimationClasses(), className)}
    >
      {children}
    </div>
  );
};

// Specific animation components
export const FadeIn: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="fadeIn" />
);

export const SlideUp: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="slideUp" />
);

export const SlideLeft: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="slideLeft" />
);

export const SlideRight: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="slideRight" />
);

export const Scale: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="scale" />
);

export const Stagger: React.FC<Omit<ScrollAnimatedProps, 'variant'>> = (props) => (
  <ScrollAnimated {...props} variant="stagger" />
);

export default ScrollAnimated;
